import { Suspense, type ReactElement } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nProvider } from './i18n/index.jsx';
import App from './App.jsx';
import { _sb } from './lib/supabase.js';
import { initMonitoring } from './lib/monitoring.js';
import { isPushSupported, registerServiceWorker } from './lib/push.js';

// ─── Lokal gehostete Schriften (DSGVO: kein Aufruf an Google Fonts) ───
// DM Sans — verwendete Stärken: 400, 500, 600, 700, 800
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/600.css';
import '@fontsource/dm-sans/700.css';
import '@fontsource/dm-sans/800.css';
import '@fontsource/dm-sans/900.css';
// Syne — verwendete Stärken: 700, 800
import '@fontsource/syne/700.css';
import '@fontsource/syne/800.css';
// JetBrains Mono — verwendete Stärken: 400, 600
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/600.css';

// ─── Leaflet Map-Library (lokal gebundelt statt unpkg-CDN) ───
import 'leaflet/dist/leaflet.css';

import './styles/global.css';
import './styles/theme-global.css';
import './styles/landing.css';

// Monitoring starten (Error-Tracking + Web Vitals → Supabase)
// _sb kann null sein wenn ENV-Vars fehlen; dann kein Monitoring.
if (_sb) {
  initMonitoring(_sb);
}

// ─── Service Worker registrieren (Push + Offline-Caching) ───
// Wir registrieren den SW erst nach dem 'load'-Event, damit der erste
// Render nicht blockiert wird. Wenn der Browser kein Web-Push kann
// (z.B. iOS Safari im normalen Tab), skippen wir stillschweigend.
// Permission wird hier NICHT abgefragt — das passiert erst, wenn der
// User in den Einstellungen aktiv "Push aktivieren" klickt.
if (isPushSupported()) {
  window.addEventListener('load', () => {
    registerServiceWorker().catch((err: Error) => {
      // Stillschweigend loggen — der User soll davon nichts merken.
      // Push-Aktivierung erfolgt später bewusst im Settings-UI.
      console.warn('[main] Service Worker registration skipped:', err.message);
    });
  });
}

/**
 * Top-Level Lade-Fallback (nur sichtbar wenn eine lazy-Seite
 * außerhalb des inneren Suspense suspendiert, z.B. beim ersten Login).
 */
function AppFallback(): ReactElement {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f7f1',
      }}>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 32,
            height: 32,
            border: '3px solid rgba(22,163,74,.15)',
            borderTopColor: '#16a34a',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px',
          }}
        />
        <div
          style={{
            fontSize: 13,
            color: 'rgba(12,26,14,.4)',
            fontFamily: "'DM Sans',sans-serif",
          }}>
          Laden…
        </div>
      </div>
    </div>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in DOM');
}
const root = ReactDOM.createRoot(rootElement);
root.render(
  // React Router 7 (21.10.2026): die früheren Zukunftsschalter
  // v7_startTransition / v7_relativeSplatPath sind in v7 Standard → entfernt.
  <BrowserRouter>
    <I18nProvider>
      <Suspense fallback={<AppFallback />}>
        <App />
      </Suspense>
    </I18nProvider>
  </BrowserRouter>,
);
